import React, { useEffect, useState } from "react";
import { backend, blogurl, categoryurl } from "../../../../path";
import { useParams } from "react-router-dom";
import LoaderMain from "../../../layout/header/loader_sub";
import { Link } from "react-router-dom";
import styled, { keyframes } from "styled-components";

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(24px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const fadeInRight = keyframes`
  from {
    opacity: 0;
    transform: translateX(40px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const fadeInScale = keyframes`
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

const PageWrapper = styled.div`
  max-width: 90vw;
  margin: 0 auto;
  padding: 0 24px 96px;
  display: flex;
  flex-direction: column;
  gap: 72px;
`;

const HeroSection = styled.section`
  margin-top: 56px;
  background: linear-gradient(135deg, #fff3e2 0%, #ffffff 65%);
  border-radius: 32px;
  padding: 48px 56px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  box-shadow: 0 40px 80px rgba(15, 23, 42, 0.08);
  animation: ${fadeInScale} 0.8s ease forwards;

  @media screen and (max-width: 768px) {
    padding: 40px 28px;
  }
`;

const BreadcrumbTrail = styled.nav`
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 14px;
  color: #6b7280;

  a {
    color: inherit;
    font-weight: 500;
  }

  span:last-child {
    color: #111827;
    font-weight: 600;
  }
`;

const HeroContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  animation: ${fadeInUp} 0.9s ease forwards;
`;

const HeroBadge = styled.span`
  display: inline-flex;
  align-items: center;
  background: rgba(98, 188, 1, 0.15);
  color: #255121;
  padding: 8px 16px;
  border-radius: 999px;
  font-size: 12px;
  letter-spacing: 1.6px;
  text-transform: uppercase;
  font-weight: 600;
`;

const HeroTitle = styled.h1`
  margin: 0;
  font-size: 44px;
  line-height: 1.1;
  color: #0f172a;

  @media screen and (max-width: 768px) {
    font-size: 34px;
  }
`;

const HeroSubtitle = styled.p`
  margin: 0;
  color: #374151;
  line-height: 1.7;
  font-size: 16px;
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1.7fr 1fr;
  gap: 32px;

  @media screen and (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const ArticleColumn = styled.article`
  display: flex;
  flex-direction: column;
  gap: 32px;
`;

const LeadMedia = styled.div`
  border-radius: 28px;
  overflow: hidden;
  box-shadow: 0 30px 70px rgba(15, 23, 42, 0.12);
  animation: ${fadeInUp} 0.8s ease forwards;

  img {
    width: 100%;
    height: 100%;
    max-height: 520px;
    object-fit: cover;
    display: block;
  }
`;

const RichText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
  font-size: 17px;
  line-height: 1.8;
  color: #17243a;
  animation: ${fadeInUp} 0.9s ease forwards;

  h2,
  h3,
  h4 {
    margin-top: 24px;
    color: #0f172a;
  }

  img {
    border-radius: 24px;
    margin: 24px 0;
    width: 100%;
    object-fit: cover;
  }
`;

const GallerySection = styled.section`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const GalleryTitle = styled.h3`
  margin: 0;
  font-size: 22px;
  font-weight: 600;
  color: #0f172a;
`;

const GalleryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 18px;

  @media screen and (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const GalleryCard = styled.div`
  border-radius: 24px;
  overflow: hidden;
  box-shadow: 0 18px 45px rgba(15, 23, 42, 0.08);
  transform: translateY(20px);
  opacity: 0;
  animation: ${fadeInUp} 0.8s ease forwards;
  animation-delay: ${({ delay }) => delay}s;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const Sidebar = styled.aside`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const SidebarCard = styled.div`
  background: #ffffff;
  border-radius: 24px;
  padding: 26px;
  box-shadow: 0 18px 45px rgba(15, 23, 42, 0.08);
  display: flex;
  flex-direction: column;
  gap: 18px;
  animation: ${fadeInRight} 0.8s ease forwards;
`;

const SidebarHeading = styled.h4`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1.2px;
  color: #0f172a;
`;

const FactsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const FactsItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const FactsLabel = styled.span`
  font-size: 12px;
  letter-spacing: 1.4px;
  text-transform: uppercase;
  color: #6b7280;
`;

const FactsValue = styled.span`
  font-size: 16px;
  font-weight: 600;
  color: #111827;
`;

const ExploreList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ExploreItem = styled.div`
  display: flex;
  align-items: center;
  padding: 12px 14px;
  border-radius: 16px;
  background: rgba(248, 250, 252, 0.9);
  box-shadow: 0 12px 24px rgba(15, 23, 42, 0.07);
  transform: translateY(20px);
  opacity: 0;
  animation: ${fadeInUp} 0.7s ease forwards;
  animation-delay: ${({ delay }) => delay}s;

  &:hover {
    background: rgba(98, 188, 1, 0.12);
    transform: translateY(-4px);
  }

  a {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    gap: 12px;
    color: #0f172a;
    font-weight: 500;

    .explore-info {
      display: flex;
      align-items: center;
      gap: 12px;
    }
  }

  img {
    width: 42px;
    height: 42px;
    border-radius: 14px;
    object-fit: cover;
  }

  span {
    font-size: 14px;
  }
`;

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  align-self: flex-start;
  padding: 10px 18px;
  border-radius: 999px;
  background: #111827;
  color: #f9fafb;
  font-weight: 600;
  font-size: 14px;
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 16px 28px rgba(15, 23, 42, 0.25);
  }
`;

const ParagraphError = styled.p`
  margin: 0;
  color: #b91c1c;
  background: #fee2e2;
  border-radius: 16px;
  padding: 16px 20px;
  font-weight: 500;
  animation: ${fadeInUp} 0.6s ease forwards;
`;

function BlogUserDetail() {
  const [data, setData] = useState(null);
  const [catData, setCatData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { id } = useParams();

  const decodeHTML = (html) => {
    const txt = document.createElement("textarea");
    txt.innerHTML = html;
    return txt.value;
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await fetch(backend + `getblog/${id}`);
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        const result = await response.json();
        setData(result);
      } catch (err) {
        setError(err);
      } finally {
        setLoading(false);
      }
    };

    const fetchCatData = async () => {
      try {
        const response = await fetch(backend + "getcategorywithsubcategory");
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        const result = await response.json();
        setCatData(result);
      } catch (err) {
        setError(err);
      }
    };

    fetchData();
    fetchCatData();
    window.scrollTo(0, 0);
  }, [id]);

  return (
    <main className="main" id="main-section">
      <PageWrapper>
        <HeroSection>
          <BreadcrumbTrail>
            <Link to="/">Home</Link>
            <span>/</span>
            <Link to="/blog">Blogs</Link>
            <span>/</span>
            <span>{data?.title || "Blog Detail"}</span>
          </BreadcrumbTrail>
          <HeroContent>
            <HeroBadge>KeralaSpecial Insight</HeroBadge>
            <HeroTitle>{data?.title || "Discover Kerala"}</HeroTitle>
            <HeroSubtitle>
              Experience the essence of Kerala through curated stories,
              traditions, and artisan journeys crafted with heart.
            </HeroSubtitle>
          </HeroContent>
        </HeroSection>

        {error && <ParagraphError>Failed to load content. Please try again.</ParagraphError>}

        <ContentGrid>
          <ArticleColumn>
            {data?.thumbnail && (
              <LeadMedia>
                <img src={blogurl + data.thumbnail} alt={data.title} />
              </LeadMedia>
            )}

            {data?.content && (
              <RichText
                dangerouslySetInnerHTML={{
                  __html: decodeHTML(data.content),
                }}
              />
            )}

            <GallerySection>
              <GalleryTitle>Visual Storyboard</GalleryTitle>
              <GalleryGrid>
                {[data?.image_1, data?.image_2, data?.image_3, data?.image_4]
                  .filter(Boolean)
                  .map((image, index) => (
                    <GalleryCard key={image + index} delay={index * 0.08}>
                      <img src={blogurl + image} alt={`${data?.title} ${index + 1}`} />
                    </GalleryCard>
                  ))}
              </GalleryGrid>
            </GallerySection>
          </ArticleColumn>

          <Sidebar>
            <SidebarCard>
              <SidebarHeading>Quick Facts</SidebarHeading>
              <FactsList>
                <FactsItem>
                  <FactsLabel>Published</FactsLabel>
                  <FactsValue>
                    {data?.created_at
                      ? new Date(data.created_at).toLocaleDateString()
                      : "Recently"}
                  </FactsValue>
                </FactsItem>
                <FactsItem>
                  <FactsLabel>Author</FactsLabel>
                  <FactsValue>{data?.author || "KeralaSpecial Team"}</FactsValue>
                </FactsItem>
              </FactsList>
            </SidebarCard>

            <SidebarCard>
              <SidebarHeading>Continue Exploring</SidebarHeading>
              <ExploreList>
                {catData.slice(0, 4).map((item, i) => (
                  <ExploreItem key={item.category_slug} delay={i * 0.06}>
                    <Link to={`/category/${item.category_slug}`}>
                      <div className="explore-info">
                        <img src={categoryurl + item.logo} alt={item.category_name} />
                        <span>{item.category_name}</span>
                      </div>
                    </Link>
                  </ExploreItem>
                ))}
              </ExploreList>
            </SidebarCard>

            <BackLink to="/blog">← Back to all stories</BackLink>
          </Sidebar>
        </ContentGrid>

        {loading && <LoaderMain />}
      </PageWrapper>
    </main>
  );
}

export default BlogUserDetail;
